/*
 * Copyright (c) 2019 LNBIG.com
 * All rights reserved.
 */

import glue          from "@/CommandGlue"
import dbg           from "debug"
import injectCaptcha from './injectCaptcha'

const debug = dbg('lnbig:store:openChannel')
const MAX_SCAN_LNURL_INDEX = 6

const stateTemplate = JSON.stringify({
    /**
     * @typedef {Object} ChannelPrice
     * @property {Object} min
     * @property {Object} max
     * @property {number} min.channelVolume - the minimum allowed volume of channel in satoshis
     * @property {number} min.priceSats - the price for min channel volume
     * @property {number} max.channelVolume - the maximum allowed volume of channel in satoshis
     * @property {number} max.priceSats - the price for max channel volume
     * @property {number} centRate - the price of usd cent in satoshis
     * @returns {Promise<void>}
     */
    /**
     * @type {ChannelPrice}
     */
    limitsAndPrices: null,
    limitsAndPricesConnectionCounter: null,
    limitsAndPricesLoading: false,
    limitsAndPricesError: null,

    prepayment: {
        invoice:         null,
        prepaid:         false,
        expires:         null,
        countdown:       null,
        error:           null,
        settled:         false,
        loading:         false,
        oc_uuid:         null,
        startedFromHere: null, // true - started from this browser, false - started from other browser (was linked here)
        startedUuid:     null, // if startedFromHere === true here will be uuid of started opening channel process
    },

    /**
     * @typedef {Object} OpenChannelInfo
     * @property {?string} lnurl - the LNURL from server
     * @property {Object}   connectMethod - if a user will use connect method (through node id / public key)
     * @property {?string}  connectMethod.anyNodeInfo - any description of node: nodeId, nodeId@host:port or invoice for example
     * @property {?boolean} connectMethod.checkStatus - true - OK, false - FAIL, null - not checked yet, {string} error - error text
     * @property {?boolean} connectMethod.checking - true - checking loading indicator, false - no checking

     * @property {?string}  connectMethod.nodeId - 33 hex string code of client's node id (defined by server)
     * @property {?string}  connectMethod.aliasNode - The alias from LN graph defined by server

     * @property {?boolean} connectManual.predictPublic - how the server defined the public/private status of future channel: true - public one, false - not public one, null - not defined
     * @property {?boolean} connectManual.userPublicDecision - how the user defined the future channel: null - not defined, true - public one, false - private one
     * @property {?boolean} connectManual.userCheckedTheNodeInfo - the user checked the own node info: false - not checked, true - checked (go to next step)

     * @property {?string}  connectMethod.nodeIdForConnect - here will be 33 hex string of our node id for connect
     * @property {?string}  connectMethod.hostForConnect - here will be host (FQDN or IP address) for connect (our server)
     * @property {?number}  connectMethod.portForConnect - here will be port number for connect (our server)

     * @property {?boolean} connectManual.clientConnected - true - the client's node has active connection to hostForConnect, false - has not, null - not defined yet
     * @property {?boolean} connectMethod.openingChannel - true - opening a channel right now, false - not opening
     * @property {?string}  connectMethod.stage - stages: 'waitNode', 'connectManual', 'openButton'
     */
    openChannelInfo: {
        lnurl:         null,
        expires:       null,
        countdown:     null,
        error:         null,
        opened:        false,
        attemptNumber: null,
        maxAttempts:   null,
        channelPoint:  null, // { tx: String, index: Number } or null

        openingMethod:   'lnurl', // types: 'lnurl', 'connect'

        connectMethod: {
            anyNodeInfo:            null,
            checkStatus:            null,
            checking:               false,

            nodeId:                 null,
            aliasNode:              null,

            predictPublic:          null,
            userPublicDecision:     null,
            userCheckedTheNodeInfo: false,

            nodeIdForConnect:       null,
            hostForConnect:         null,
            portForConnect:         null,

            clientConnected:        null,
            openingChannel:         false,
            stage:                  null,
        }
    },

    startTimeForLnurlCountdown:      null, // time (ms) from epoch when the LNURL was changed (for correct time of countdown)
    startTimeForPrepaymentCountdown: null, // time (ms) from epoch when the prepayment was changed (for correct time of countdown)
})

let mutations = {
    cmdSetPrepayment(state, prepayment) {
        Object.assign(state.prepayment, prepayment)
        if (state.prepayment.startedFromHere === true && prepayment.oc_uuid)
            state.prepayment.startedUuid = prepayment.oc_uuid
    },
    cmdSetOpenChannelInfo(state, openChannelInfo) {
        Object.assign(state.openChannelInfo, openChannelInfo)
    },
    cmdSetConnectMethod(state, connectMethodData) {
        debug('cmdSetConnectMethod, connectMethodData: %o', connectMethodData)
        Object.assign(state.openChannelInfo.connectMethod, connectMethodData)
    },
    cmdSetLimitsAndPricesAsync(state, message) {
        state.limitsAndPricesLoading = false
        glue.find(
            'cmdSetLimitsAndPricesAsync',
            message,
            (message) => {
                state.limitsAndPrices = message.limitsAndPrices
                state.limitsAndPricesConnectionCounter = this.state.connectionCounter
            }
        )
    },
    cmdSetLimitsAndPrices(state, message) {
        state.limitsAndPrices = message.limitsAndPrices
        state.limitsAndPricesConnectionCounter = this.state.connectionCounter
    },
    cmdSetLimitsAndPricesError(state, message) {
        state.limitsAndPricesError = message
    },
    cmdResetState(state, initObjects) {
        Object.assign(state, JSON.parse(stateTemplate))
        if (initObjects) {
            mutations.cmdSetLimitsAndPrices.call(this, state, initObjects.limitsAndPrices)
            mutations.cmdSetPrepayment.call(this, state, initObjects.prepayment)
            mutations.cmdSetOpenChannelInfo.call(this, state, initObjects.openChannelInfo)
        }
    },
    resetForStartOpenChannelProcess(state) {
        let initObjects = JSON.parse(stateTemplate)

        mutations.cmdSetPrepayment.call(this, state, initObjects.prepayment)
        mutations.cmdSetOpenChannelInfo.call(this, state, initObjects.openChannelInfo)
        state.startTimeForLnurlCountdown = initObjects.startTimeForLnurlCountdown
    },
    setStartTimeForLnurlCountdown(state) {
        state.startTimeForLnurlCountdown = Date.now()
    },
    setStartTimeForPrepaymentCountdown(state) {
        state.startTimeForPrepaymentCountdown = Date.now()
    },
    resetLimitsAndPricesConnectionCounter(state) {
        state.limitsAndPricesConnectionCounter = null
    },
    setLimitsAndPricesLoading(state, value) {
        state.limitsAndPricesLoading = value
    },
}

let obj = {
    namespaced: true,
    state: JSON.parse(stateTemplate),

    mutations,

    actions: {
        /* TODO
        *   Проверить случаи, когда сессия связывается, затем открытие канала заканчивается - что происходит с ценами?
        *   Надо сделать перезагрузку цен
        * */
        async getLimitsAndPrices({rootState, state, commit}) {
            commit('setLimitsAndPricesLoading', true)
            if (rootState.connectionCounter !== state.limitsAndPricesConnectionCounter || ! state.limitsAndPrices) {
                debug('getLimitsAndPrices - loading from server')
                return glue.add(
                    (uuid) => {
                        rootState.webSocket.sendObj({
                            command: 'cmdGetLimitsAndPricesAsync',
                            namespace: 'openChannel',
                            uuid,
                        })
                    },
                    'cmdSetLimitsAndPricesAsync'
                )
                .catch(
                    e => {
                        commit('cmdSetLimitsAndPricesError', e.message)
                    }
                )
            }
        },

        async reloadLimitsAndPrices({dispatch, commit}) {
            commit('resetLimitsAndPricesConnectionCounter')
            await dispatch('getLimitsAndPrices')
        },

        // Это действие вызывается компонентом open-channel-wizard
        async startOpenChannelProcess({rootState, state, commit, dispatch}, requestSats) {
            commit('resetForStartOpenChannelProcess')
            commit('cmdSetPrepayment', { startedFromHere: true, loading: true })
            await dispatch('checkHelloAndRecaptcha')
            await dispatch('getLimitsAndPrices')
            rootState.webSocket.sendObj({
                command: 'cmdStartOpenChannelProcess',
                namespace: 'openChannel',
                requestSats
            })
        },

        // Это действие вызывается компонентом open-channel-wizard
        async linkOpenChannelProcess({rootState, state, commit, dispatch}, oc_uuid) {
            commit('cmdSetPrepayment', { startedFromHere: false, loading: true })
            await dispatch('checkHelloAndRecaptcha')
            rootState.webSocket.sendObj({
                command: 'cmdLinkOpenChannelProcess',
                namespace: 'openChannel',
                oc_uuid
            })
        },

        // Это действие вызывается компонентом open-channel-wizard
        async connectMethodCommand({rootState, dispatch}, data) {
            await dispatch('checkHelloAndRecaptcha')
            rootState.webSocket.sendObj({
                command: 'cmdConnectMethodCommand',
                namespace: 'openChannel',
                data
            })
        },

        async afterSocketRestarted({commit}) {
            commit('setReCaptchaV3Promise', null)
            commit('setReCaptchaV2Promise', null)

        },

        async restartOpenChannel({commit, dispatch}) {
            debug('restartOpenChannel called')
            commit('cmdResetState')
            await dispatch('getLimitsAndPrices')
        }
    },
}

obj = injectCaptcha(obj, 'openChannel', 'openChannelWizard')

debug('После injectCaptcha, openChannel: %o', obj)

export default obj
